import { useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { useNewVersionAvailable } from '@/hooks/useNewVersionAvailable';

interface NewVersionBannerProps {
  /** Injected for tests; defaults to a full page reload. */
  onReload?: () => void;
}

/**
 * NewVersionBanner - Non-blocking notice shown once a newer frontend build is
 * detected on the server while this tab is still running an older one.
 *
 * Lazy-loaded route chunks are pinned to the build the tab started with, so
 * fixes do not reach an open tab until it reloads. The banner only suggests a
 * reload; it never forces one, so in-progress work is not lost. Anchored to the
 * bottom-left corner because the bottom-right is used by GlobalProgressWidget
 * and SupportChatWidget.
 */
export function NewVersionBanner({
  onReload = () => window.location.reload(),
}: NewVersionBannerProps) {
  const available = useNewVersionAvailable();
  const [dismissed, setDismissed] = useState(false);

  if (!available || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-4 z-[90] max-w-[calc(100vw-6rem)] sm:max-w-lg"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 shadow-lg">
        <RefreshCw className="h-4 w-4 shrink-0 text-amber-600" />
        <span className="min-w-0 flex-1">
          새 버전이 배포되었습니다. 새로고침하면 최신 화면으로 전환됩니다.
        </span>
        <button
          type="button"
          onClick={onReload}
          className="rounded bg-amber-600 px-3 py-1 font-medium text-white transition-colors hover:bg-amber-700"
        >
          새로고침
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="새 버전 안내 닫기"
          className="rounded p-1 text-amber-700 transition-colors hover:bg-amber-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
